// 体幹・HP経済 A/B プリセット (C:/dev/LUNAIRE-notes/reports/2026-07-03-hp-posture-economy-tuning.md §3.2)。
// v2=既定 (2026-07-04 実プレイ承認でデフォルト昇格・下の `let preset`) / v1=balance.ts の素の値 (旧経済)。
// __econ('v1') (main.ts) で旧経済へ切替。 切替はボスの次回スポーンから反映 (state.ts 側)。
// 実効値の一覧 = docs/generated/econ-current-values.md (tests/econ_snapshot.test.ts が生成)。

import { BOSS, COMBAT, PLAYER } from './balance'

export type EconPreset = 'v1' | 'v2'

type EconValues = {
  bossPostureRecoveryPerSec: number
  // 体幹ゲイン後この秒数はボス体幹が回復しない (0=常時回復=v1 従来挙動)。
  //   SEKIRO 型「攻め続ける限り体幹は戻らない・手を止めた時だけ戻る」の中核 (2026-07-04 v2改)。
  bossPostureRegenLockSec: number
  bossHpDamageOpen: number
  bossPostureGainOpen: number
  normalParrySelfPostureGain: number
  // v4 (2026-07-04 ユーザー裁定): 見切りカウンターのHPダメ。 v1=COMBAT.counterAttack.bossHpDamage (=6)。
  bossHpDamageCounter: number
  // v5 (2026-07-04 ユーザー裁定): ボスごとのHP/体幹プール上書き (旧 knightMaxHp 等の汎用化)。
  //   キー=BossDefinition.id。 指定フィールドだけ上書き・未指定/未登録ボスは def のまま。 全値⬜暫定
  //   (騎士承認値からの同率反映 ×1.4/×1.45・boss-slice 時に本調整)。
  bossPools?: Record<string, {
    maxHp?: number
    maxPosture?: number
    roomHpFractions?: number[]
    finalRoomPostureMult?: number
    // ★2026-07-29: 体幹の「猶予」= 弾いた/当てた直後、減衰が止まっている秒数。
    //   ボスごとの手触りの中心。長い = 拍を跨いで積める (重い単発ボス) /
    //   短い = 連撃の中で繋がないと積めない (花嫁のような連撃ボス)。
    //   ⚠️ 秒単位で貯金させない (ユーザー裁定: 5回弾いて1分回避して最後の1回で倒せるのはおかしい)。
    //   未指定は下の bossPostureRegenLockSec (全ボス共通の既定) を使う。
    postureRegenLockSec?: number
    // ★2026-07-30 (ユーザー裁定=案2): 体幹の**減る速さ**をボス単位で持つ (毎秒)。
    //   譜面を厚くしても体幹で倒せない原因は「積む量」ではなく「減る量」だった。
    //   実測: 刑吏は弾き50回×25=1250 に対し、減衰が 20/秒×163秒=3260 で2.6倍勝っていた。
    //   理屈: **重い道具のボスは拍が遅い = 戻りも鈍い**。見た目と経済を同じ向きに揃える。
    //   未指定は下の bossPostureRecoveryPerSec (全ボス共通の既定) を使う。
    postureRecoveryPerSec?: number
    // 幕HP残率の下限と回復倍率。未指定は共通表を使う。
    postureRecoveryCurve?: ReadonlyArray<readonly [number, number]>
    // ★2026-10-08 (戴冠獣の比較の段 c だけ・src/hound_tune.ts): 幕の体力が減るほど指数で効く連続の曲線。
    //   postureRecoveryExp = k: 減る速さの倍率 = max(床, e^(−k×(1−幕の体力の残り)))。指定時は上の段階表より優先。
    //   parryGainHpExp = g: パリィで溜まる量の倍率 = e^(g×(1−幕の体力の残り)) (弾きだけ・殴りには掛けない)。
    //   ユーザー 10-08「体力によって溜まりやすく設定しているのをもっと強い指数関数的に調整して」。
    postureRecoveryExp?: number
    parryGainHpExp?: number
  }>
}

// v4実プレイ承認 (2026-07-04): デフォルト昇格。 __econ('v1') で旧経済に戻せる。
let preset: EconPreset = 'v2'

export function setEconPreset(p: EconPreset): void {
  preset = p
}

export function getEconPreset(): EconPreset {
  return preset
}

const V1: EconValues = {
  bossPostureRecoveryPerSec: BOSS.postureRecoveryPerSec,
  bossPostureRegenLockSec: 0,
  bossHpDamageOpen: COMBAT.playerAttack.bossHpDamageOpen,
  bossPostureGainOpen: COMBAT.playerAttack.bossPostureGainOpen,
  normalParrySelfPostureGain: PLAYER.normalParrySelfPostureGain,
  bossHpDamageCounter: COMBAT.counterAttack.bossHpDamage,
}

// v2改 (2026-07-04 実プレイフィードバック反映): 旧v2 (回復15/s 常時) は幕HP満タンで
//   パリィが積み上がらず「削るしかない」経済になった → 回復9/s+ゲイン後1.5s回復停止へ。
//   体幹=メインの倒しゲージ (騎士160)・HP削り=回復を鈍らせる支援+削り切りも選択肢。
// v3〜v5 (2026-07-04 ユーザー裁定): 歴史=git log 参照 (体幹ゲインを幕HP率スケールする
//   「gain-wall」を経由・v6 (①) でロック中部分回復へ・②(2026-07-16)で撤去)。
// v6 (①体幹ゲート修正・2026-07-16): gain-wall (体幹ゲインの幕HP率スケール) で turtle を止めたが
//   実機で「SEKIROと違う・殴りが勝ち筋にならない」と判明→②で撤去。
// ②本物のSEKIRO化 (2026-07-16 ユーザー裁定=A案): gain-wallを廃し、 turtle対策を
//   「体幹回復」側へ一本化 (bossPostureRecoveryPerSec を上げ、 回復ロックを撤廃=常時HP連動回復)。
//   通常パリィの自体幹増加を下げ (17→11) ズレの罰を緩める。 数値は計測ゲート
//   (tests/combat_sim/boss_measurement.test.ts の turtle/aggression/justParry 3bot) で検証済。
const V2: EconValues = {
  bossPostureRecoveryPerSec: 20,
  // ★2026-07-29: 0 → 0.25。体幹の「猶予」の全ボス共通の既定 (秒)。掃きで 0.15〜0.8 を比較し、
  //   0.15 でも構造問題 (連撃なしボスが積めない) は解けたので、「貯金」から遠い短い側を採った。
  //   0 だと拍と拍の間に必ず減衰が走り、連撃を持たないボスは構造的に体幹が積めなかった。
  //   ボスごとに bossPools[].postureRegenLockSec で上書きする (形は共通・量はボスごと)。
  bossPostureRegenLockSec: 0.25,
  bossHpDamageOpen: 2,
  bossPostureGainOpen: 10,
  normalParrySelfPostureGain: 11,
  bossHpDamageCounter: 3,
    // ⬜暫定 (2026-07-29 経済改修): 1戦の長さを「2〜3分・ラスボスへ向かって伸びる」へ寄せた
    //   (ユーザー裁定 B)。旧プールは全体に 1戦26〜67秒 = 目標の 1/3 だった。
    //   ⚠️ **時間と「体幹で倒すかHPで倒すか」は独立に動く**ことを実測で確認済 (economy_sweep)。
    //      プールを何倍にしても「崩れた瞬間のHP残%」はほぼ変わらない → 時間だけをここで合わせる。
    //   計器 = tests/combat_sim/economy_probe.test.ts / 掃き = economy_sweep.test.ts
    //   🔵 2026-09-04 ユーザー裁定で縮小: 決着時間を全ボスで揃える (勝ち試合 90〜150秒 / bot 75〜100秒)。
    //     倍率 = 王 0.65 / 騎士 0.75 / 花嫁 0.85 (HPと体幹を同率)。介錯は既に帯の中なので触らない。
    //     下限の条件 = 「リトライする中で技の8割を見られること」(ユーザーの言葉)。実測で、縮めても
    //     8割到達までの試合数は最大 +1戦 (騎士のみ 1戦→2戦) = 技の見え方は制約にならないと確認済み。
    //     計器 = tests/combat_sim/move_coverage_sweep.test.ts
    //   ⚠️ 縮小前に守り勝ちゲートを確認済み (tests/combat_sim/pool_shrink_gate_probe.test.ts)。
    //     6セル全てで turtle 0勝 / 全回避は明確に不利。2026-07-16 に閉じた「弾かずに守り勝つ道」は開いていない。
    //   ⬜ これは一旦の決定。実プレイの体感で動かす前提 (人間の実測はまだゼロ)。
  bossPools: {
    // 稽古の亡霊 = 体幹の減る速さだけ 20→8/秒 (2026-10-05 ユーザー裁定「減りを遅くする」)。
    //   既定の 20/秒だと、カードの通りに弾くだけの人は仕上げでジャストを何回重ねても崩れなかった
    //   (体力 55% で入ると 13.2/秒で減り、+25 が斬りの周期 2.5 秒の間に消える)。
    //   8/秒で「弾く→帯が溜まる→ジャストで割れる」を殴らずに一周できる。体力・体幹の量は bosses.ts のまま。
    //   調査＝docs/research/2026-10-05-shade-practice-stagger.md／守るテスト＝tests/combat_sim/practice_stagger_gate.test.ts
    //   戻す＝この行を消す (全ボス共通の 20/秒へ落ちる)。
    'nameless-shade': { postureRecoveryPerSec: 8 },
    // 王 (最初の本ボス) = 旧130/130 の ×3。短めに置く (最初の1体なので目標帯より下でよい)。
    // 🔵 2026-10-08 ユーザー採用 (kensyu-hound-tune2.html：幕2の三連・版 c とも「はい」) = 配布版の既定 = 段 c (src/hound_tune.ts)。
    //   体力 240 (幕ごと 120)・ゲージ 254・猶予 0.5 秒・減る速さの倍率を幕の体力で指数に e^(−3×削った割合)・
    //   弾き1回の量も指数で増やす e^(0.6×削った割合) (弾きだけ・殴りには掛けない)。
    //   実測 (justParryBot・幕2の三連あり・100戦) ≈56秒・回避だけの bot は 300 秒以内に 4/100 勝・弾くだけの bot 0 勝。
    //   旧 (〜10-08 = 体力 254・猶予 0.25・段階の表) は DEV ?houndTune=now。記録 = docs/research/2026-10-08-hound-hp-posture-tune.md。
    'dream-hound': { maxHp: 240, maxPosture: 254, postureRegenLockSec: 0.5, postureRecoveryExp: 3, parryGainHpExp: 0.6 },
    // 花嫁 = def 側 (HP210/体幹130) をここで上書き (HP は約1.7倍)。旧コメントの「×2・体幹 150→300」は古い。
    //   実効値は下の行／docs/generated/econ-current-values.md を参照。
    //   finalRoomPostureMult は 2026-07-29 追加。幕3が97%残しで崩れる (=弾きだけで即終わる) のを抑える。
    'bride': { maxHp: 357, maxPosture: 255, finalRoomPostureMult: 0.70 },   // 実測 ≈104秒 (bot)
    // 騎士 = 旧210/160 の ×2.5。
    'vigil-knight': { maxHp: 394, maxPosture: 300, roomHpFractions: [50, 70, 90], finalRoomPostureMult: 0.8 },   // 実測 ≈93秒 (bot)
    // 刑吏 = 旧120/140 の ×2.8。4体目なので騎士より少し長く。
    //   ⚠️ 幕1は刃の据えの一打 (3連) 以外に弾ける連撃が無く、HP で進む幕 (設計どおり)。
    //   ★postureRecoveryPerSec 9 (既定20の半分以下) = 2026-07-30 掃きで決定。
    //     譜面13本を入れて弾きが 37→50回 に増えても崩れHP残が動かず、原因は「積む量」ではなく
    //     「減る量」だった (弾き50×25=1250 vs 減衰20×163秒=3260)。掃き結果 (40試行):
    //       20/秒 → 165秒 崩れHP残 1/22/24%   13/秒 → 145秒 2/34/41%
    //        9/秒 → 132秒 8/40/52%            7/秒 → 127秒 10/43/54%
    //     9 を採用 = 戦闘2.2分 (目標2〜3分の下側・4体目として妥当) で「幕が進むほど体幹寄り」が出る。
    //   ★理屈: **技の平均サイクルが全ボス最長 (2.5 s) なので戻りを遅くしないと積めない**。見た目と経済を同じ向きに揃える
    //     (ユーザー裁定 2026-07-30 案2「ボスごとに減衰を変える」)。
    //   ⬜幕1が8%残しなのは既知。幕1は「刃だけで読みを確立する幕」として許容している。HP で進む (📏 崩れ HP 30%)。
    //   ★2026-09-22 体幹 392→360 (−8%・一打あたりの溜まり +9%)。ユーザー実機「ちょっと長いけど短くしすぎたくない。
    //     ほんの少し体幹溜まりやすくして」(検収パック 2026-09-22 A-1)。戻す＝392。
    'the-executioner': { maxHp: 336, maxPosture: 360, postureRecoveryPerSec: 9 },   // 実測 ≈132秒 (392 時点)
    // ⬜暫定 (2026-07-29): 未着手のボスも、新しい経済 (猶予つき) では旧プールだと軽すぎるため
    //   一律 ×2.5 で揃えた。**個別の手触り調整は各ボスの制作時に行う** (ここは仮の受け皿)。
    //   ⚠️ 揃えないと「弾くだけで殴らないbot」に勝たれる (boss_measurement の守り勝ちゲート)。
    // 2026-09-13 試行：その幕のHPを約25%削ると、弾いた体幹が残りやすくなる。
    // 満タン側25/s、75%未満で11/sへ。HP/体幹の総量は別途検討。
    'crowned-king-awoken': {
      maxHp: 525, maxPosture: 363, postureRecoveryPerSec: 25,
      postureRecoveryCurve: [[0.75, 1], [0.50, 0.44], [0.25, 0.264]],
    },
    // 鶏 (10-31 数値合わせ・docs/research/2026-10-31-rooster-econ-tune.md)。全ボス共通の物差し (初演・justParry で体幹が満タンになった時の幕の体力の残り) で
    //   前 388 (仮の ×2.5) = 82%/73% と他ボス (48〜66%) より削れず、満タン後も長く殴る形だった。180・取り分 45:55 で 60%/59%。体幹 350 は据え置き。
    //   前の値 = 開発版 ?roosterHp=old (setRoosterPoolOverride)。
    'inverted-jester': { maxHp: 180, maxPosture: 350, roomHpFractions: [45, 55] },
    'blade-dancer': { maxHp: 525, maxPosture: 325 },
    'hollow-actor': { maxHp: 625, maxPosture: 363 },
    'former-princess': { maxHp: 625, maxPosture: 375 },
  },
}

export function econ(): EconValues {
  return preset === 'v2' ? V2 : V1
}

// 体幹の「猶予」秒数をボス単位で解決する (2026-07-29)。
//   猶予 = 弾いた/当てた直後、体幹の減衰が止まっている時間。
//   ボスごとの体幹経済は「形は共通・量はボスごと」で作る (ユーザー裁定 2026-07-29)。
//   ここは「量」側なので bossPools で上書きでき、未指定は全ボス共通の既定へ落ちる。
export function bossPostureGraceSec(bossId: string): number {
  return econ().bossPools?.[bossId]?.postureRegenLockSec ?? econ().bossPostureRegenLockSec
}

// 体幹の「減る速さ」(毎秒) をボス単位で解決する (2026-07-30・ユーザー裁定=案2)。
//   猶予 (bossPostureGraceSec) が「いつ減り始めるか」、こちらが「どれだけ速く減るか」。
//   2つ揃って初めてボスごとの体幹経済になる。未指定は全ボス共通の既定へ落ちる。
export function bossPostureRecoveryPerSec(bossId: string): number {
  return econ().bossPools?.[bossId]?.postureRecoveryPerSec ?? econ().bossPostureRecoveryPerSec
}

// 戴冠獣の体力・体幹の比較の段 (2026-10-08・src/hound_tune.ts の setHoundTune だけが呼ぶ)。
//   配布版の既定 (上の bossPools['dream-hound']) を覚えておき、段の上書きを重ねて置き直す。null = 既定へ戻す。
//   切替はボスの次回スポーンから反映 (state.ts createBossFromDefinition が bossPools を読む)。
type BossPool = NonNullable<EconValues['bossPools']>[string]
const HOUND_POOL_RELEASE: BossPool = { ...V2.bossPools!['dream-hound']! }
// 騎士の2幕 (2026-10-09 作成・10-10 から配布版の既定・旧3幕は DEV ?knightActs=3。src/knight_acts.ts の setKnightActs だけが呼ぶ)。
//   幕を 3→2 にすると致命が1回減り、同じ体幹 (300) だと戦いが短くなる。「今の騎士と戦い全体の長さが近い」所に置いた仮の値。
//   ⬜ 全ボス共通の「幕の体力 50〜60% で体幹が満タン」(?econBase=1・別の係) に合わせる時は、ここの4つだけを動かす。
//   幕の数・技の振り分けは knight_acts.ts。記録 = docs/research/2026-10-09-knight-two-acts-status.md。
export const KNIGHT_TWO_ACT_POOL: BossPool = { maxHp: 394, maxPosture: 430, roomHpFractions: [55, 45], finalRoomPostureMult: 0.8 }
const KNIGHT_POOL_RELEASE: BossPool = { ...V2.bossPools!['vigil-knight']! }
export function setVigilKnightPoolOverride(over: Partial<BossPool> | null): void {
  V2.bossPools!['vigil-knight'] = { ...KNIGHT_POOL_RELEASE, ...(over ?? {}) }
}

// 鶏の体力の比較 (10-31・開発版 ?roosterHp=old だけが呼ぶ)。old = 前の 388・取り分なし (均等)。null = 配布版へ戻す。
export const ROOSTER_POOL_OLD: BossPool = { maxHp: 388, maxPosture: 350 }
const ROOSTER_POOL_RELEASE: BossPool = { ...V2.bossPools!['inverted-jester']! }
export function setRoosterPoolOverride(pool: BossPool | null): void {
  V2.bossPools!['inverted-jester'] = { ...(pool ?? ROOSTER_POOL_RELEASE) }
}

export function setDreamHoundPoolOverride(over: Partial<BossPool> | null): void {
  const pool: Record<string, unknown> = { ...HOUND_POOL_RELEASE, ...(over ?? {}) }
  // 段が undefined を明示した欄 (旧の値＝指数の曲線なし) は消す (未指定＝全ボス共通の既定へ落ちる)。
  for (const k of Object.keys(pool)) if (pool[k] === undefined) delete pool[k]
  V2.bossPools!['dream-hound'] = pool as BossPool
}

// dev つまみ (2026-09-01): 覚醒王の postureRecoveryPerSec を URL から上書きする
//   (`?awokenPosture=<n>` = src/game.ts)。1戦の長さの犯人がこの値だと判った
//   (docs/reports 参照・40試行掃きで HPプールでなくこちらが律速)。次のプレイで 20/13/11 を比べる。
//   異常値 (数値でない・0以下) は無視して bossPools の既定 (未設定=全ボス共通の 20) のまま。
export function setAwokenKingPostureRecoveryPerSec(value: number): void {
  if (!Number.isFinite(value) || value <= 0) return
  const pool = V2.bossPools?.['crowned-king-awoken']
  if (pool) {
    pool.postureRecoveryPerSec = value
    // 明示URLは従来20/13/11との比較。新しいHP連動候補は通常起動で試す。
    delete pool.postureRecoveryCurve
  }
}
